import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Sparkles, Phone, Mail, Calendar, Clock, DollarSign } from "lucide-react";
import api from "../lib/api";
import { FadeUp } from "../components/Motion";

export default function Account() {
  const [params] = useSearchParams();
  const emailParam = (params.get("email") || "").trim();
  const [emailInput, setEmailInput] = useState(emailParam);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!emailParam) return;
    load(emailParam);
  }, [emailParam]);

  const load = async (e) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/yard-designer/account?email=${encodeURIComponent(e)}`);
      setData(r.data);
    } catch (e2) {
      setErr(e2?.response?.status === 404 ? "No designs found for that email." : "Could not load account.");
      setData(null);
    } finally { setLoading(false); }
  };

  const submitLookup = (e) => {
    e.preventDefault();
    if (emailInput.includes("@")) load(emailInput.trim());
  };

  const inputCls = "w-full bg-[#151515] border border-[#D8D6D0]/20 px-4 py-3 text-sm text-[#F5F3EF] focus:outline-none focus:border-[#F5F3EF]/70";

  // ----- Lookup form when no email yet -----
  if (!data) {
    return (
      <div data-testid="account-page" className="pt-32 pb-32 px-6 md:px-12 lg:px-16">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Your Account</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none max-w-3xl">
            View your <span className="italic text-[#D8D6D0]">designs.</span>
          </h1>
          <p className="text-[#D8D6D0]/60 mt-6 max-w-xl">
            Enter the email you used in the AI Yard Designer to see your concepts and selection status.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <form onSubmit={submitLookup} className="nc-card mt-12 p-8 max-w-xl space-y-4">
            <input
              required
              type="email"
              placeholder="Email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className={inputCls}
              data-testid="account-email-input"
            />
            <button type="submit" disabled={loading} data-testid="account-lookup" className="w-full px-6 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm uppercase tracking-widest disabled:opacity-50">
              {loading ? "Loading…" : "View My Designs"}
            </button>
            {err && <p className="text-xs text-[#8C1D30] mt-3" data-testid="account-error">{err}</p>}
            <p className="text-xs text-[#D8D6D0]/50 leading-relaxed pt-2">
              Don't have a design yet? <Link to="/yard-designer" className="link-underline text-[#F5F3EF]">Try the AI Yard Designer →</Link>
            </p>
          </form>
        </FadeUp>
      </div>
    );
  }

  // ----- Account view -----
  const allConcepts = data.generations.flatMap((g) =>
    (g.concepts || []).map((c) => ({ ...c, generation_id: g.id, selected: g.selected_concept_label === c.label }))
  );
  const selectedSet = data.generations
    .filter((g) => g.selected_concept_label)
    .map((g) => {
      const c = (g.concepts || []).find((x) => x.label === g.selected_concept_label);
      return c ? { ...c, generation_id: g.id, generation_form: g.form, selected_at: g.selected_at } : null;
    })
    .filter(Boolean);

  return (
    <div data-testid="account-page" className="pt-32 pb-32 px-6 md:px-12 lg:px-16">
      <FadeUp>
        <div className="overline text-[#8C1D30] mb-4">Your Account</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none">
          Welcome back, <span className="italic text-[#D8D6D0]">{data.name || "there"}.</span>
        </h1>
        <p className="text-[#D8D6D0]/60 mt-4 text-sm">{data.email} · {data.phone}</p>
      </FadeUp>

      {/* Selected concept highlight */}
      {selectedSet.length > 0 && (
        <FadeUp delay={0.1}>
          <div className="mt-12 nc-card p-6 md:p-10 border-l-2 border-[#8C1D30]">
            <div className="overline text-[#8C1D30] mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Your Selection
            </div>
            {selectedSet.map((c) => (
              <div key={c.generation_id + c.label} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-7 aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
                  {c.image_base64 ? (
                    <img src={`data:image/png;base64,${c.image_base64}`} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/40 text-sm">Render unavailable</div>
                  )}
                </div>
                <div className="md:col-span-5">
                  <div className="overline text-[#D8D6D0]/60 mb-2">Concept {c.label}</div>
                  <h2 className="font-serif-display text-3xl md:text-4xl mb-3">{c.title}</h2>
                  <p className="text-[#D8D6D0]/70 leading-relaxed">{c.description}</p>
                  <div className="mt-6 grid grid-cols-2 gap-4 pt-6 border-t border-[#D8D6D0]/10">
                    <div>
                      <div className="overline text-[#D8D6D0]/50 flex items-center gap-2"><DollarSign className="w-3 h-3" /> Budget</div>
                      <div className="font-serif-display text-lg mt-1">{c.budget_estimate}</div>
                    </div>
                    <div>
                      <div className="overline text-[#D8D6D0]/50 flex items-center gap-2"><Clock className="w-3 h-3" /> Timeline</div>
                      <div className="font-serif-display text-lg mt-1">{c.timeline_estimate}</div>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-[#D8D6D0]/10 text-xs text-[#D8D6D0]/60 flex items-center gap-2">
                    <Calendar className="w-3 h-3" /> Selected {new Date(c.selected_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-8 pt-8 border-t border-[#D8D6D0]/10 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3 mb-2 text-sm text-[#D8D6D0]/80">
                <span className="text-[#8C1D30]">●</span> A senior NorthCrest designer is reviewing your selection and will reach out within 24 business hours.
              </div>
              <a href="tel:+17806557604" className="flex items-center justify-center gap-2 px-4 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest">
                <Phone className="w-4 h-4" /> Call us now
              </a>
              <a href="mailto:northcrestedmonton@gmail.com?subject=Yard Design Follow-up" className="flex items-center justify-center gap-2 px-4 py-3 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors">
                <Mail className="w-4 h-4" /> Email designer
              </a>
              <Link to="/projects" className="flex items-center justify-center gap-2 px-4 py-3 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors">
                <Sparkles className="w-4 h-4" /> View portfolio
              </Link>
            </div>
          </div>
        </FadeUp>
      )}

      {/* All concepts grid */}
      <FadeUp delay={0.15}>
        <div className="mt-16 overline text-[#D8D6D0]/50 mb-6">All Your Concepts</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allConcepts.map((c) => (
            <div key={c.generation_id + c.label} data-testid={`account-concept-${c.label}`} className={`relative overflow-hidden border ${c.selected ? "border-[#8C1D30]" : "border-[#D8D6D0]/10"}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
                {c.image_base64 ? (
                  <img src={`data:image/png;base64,${c.image_base64}`} alt={c.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/40 text-xs">No render</div>
                )}
              </div>
              {c.selected && (
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-[#8C1D30] text-[#F5F3EF] text-[10px] uppercase tracking-widest">
                  <CheckCircle2 className="w-3 h-3" /> Selected
                </div>
              )}
              <div className="p-5">
                <div className="overline text-[#D8D6D0]/50 mb-1">Concept {c.label}</div>
                <div className="font-serif-display text-xl">{c.title}</div>
                <div className="mt-3 text-xs text-[#D8D6D0]/60">{c.budget_estimate} · {c.timeline_estimate}</div>
              </div>
            </div>
          ))}
        </div>
      </FadeUp>
    </div>
  );
}
